import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { Modal } from '../../components/Modal';
import { FotoProduto } from '../../components/FotoProduto';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useToast } from '../../components/Toast';
import { UpperInput } from '../../components/inputs/UpperInput';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { useGridNavegavel } from '../../lib/useGridNavegavel';
import { MenuAcoesLinha, type AcaoLinha } from '../../components/MenuAcoesLinha';
import { StatusBadge } from '../../components/StatusBadge';
import { Pencil, Ban, CheckCircle2, ChevronUp, ChevronDown, ChevronsUpDown, Merge, Plus, Minus } from 'lucide-react';
import { compararDescricaoNatural } from '@fiodecobre/shared';

type ColunaOrdem = 'codigo' | 'descricao' | 'produtos' | 'status';

interface Local {
  id: number;
  codigo: string;
  descricao: string | null;
  ordem: number;
  ativo: boolean;
  produtos: number;
}

interface ProdutoDoLugar {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  status: string;
  marca_nome: string | null;
  unidade_sigla: string | null;
  estoque_efetivo: string | null;
  principal: boolean;
  outros_lugares: string | null;
}

export function Localizacoes() {
  const toast = useToast();
  const [itens, setItens] = useState<Local[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [mostrarInativas, setMostrarInativas] = useState(false);
  const [editando, setEditando] = useState<Local | null>(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ codigo: '', descricao: '' });
  const [confirmar, setConfirmar] = useState<Local | null>(null);
  const [marcadas, setMarcadas] = useState<Set<number>>(new Set());
  const [modalMesclar, setModalMesclar] = useState(false);
  const [sobrevivente, setSobrevivente] = useState<number | null>(null);
  const [mesclando, setMesclando] = useState(false);
  // O "+" da linha (pedido dele, 08/10/2026): quem mora nesta prateleira, com foto. Molde do expand
  // da aba "Vendas hoje" do Caixa — mesmo botao, mesmo inverter de cor, mesmo lazy load: sao ~125
  // lugares e quase nenhum vai ser aberto.
  // `undefined` = nunca buscou · `null` = buscando · `[]` = vazio — tres estados diferentes na tela.
  const [abertos, setAbertos] = useState<Set<number>>(new Set());
  const [produtosPorLugar, setProdutosPorLugar] = useState<Record<number, ProdutoDoLugar[] | null>>({});

  // Reusa `GET /:id/produtos`, a mesma rota da tela Localizacoes por Produtos (le a RELACAO, entao
  // traz tambem quem tem este lugar como endereco secundario). Com `incluir_inativos` pra lista
  // bater com a coluna Produtos, que conta TODO vinculo — o inativo vai marcado na linha.
  function alternarProdutos(id: number) {
    setAbertos((atual) => {
      const novo = new Set(atual);
      if (novo.has(id)) novo.delete(id); else novo.add(id);
      return novo;
    });
    if (produtosPorLugar[id] !== undefined) return;
    setProdutosPorLugar((m) => ({ ...m, [id]: null }));
    api.get<ProdutoDoLugar[]>(`/api/localizacoes/${id}/produtos`, { query: { incluir_inativos: true } })
      .then((lista) => setProdutosPorLugar((m) => ({ ...m, [id]: lista })))
      .catch((e: any) => {
        toast.erro(e.message);
        // Volta a "nunca buscou": o proximo clique tenta de novo em vez de mostrar "vazio" mentindo.
        setProdutosPorLugar((m) => { const n = { ...m }; delete n[id]; return n; });
        setAbertos((a) => { const n = new Set(a); n.delete(id); return n; });
      });
  }

  async function carregar() {
    setCarregando(true);
    try {
      const lista = await api.get<Local[]>('/api/localizacoes', { query: { incluir_inativos: mostrarInativas } });
      setItens(lista);
      // Mesclar/editar mudam quem mora onde: a lista guardada do "+" ficaria velha.
      setProdutosPorLugar({});
      setAbertos(new Set());
    } catch (e: any) { toast.erro(e.message); }
    finally { setCarregando(false); }
  }
  useEffect(() => { void carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [mostrarInativas]);

  // Ordenar pelo cabecalho (pedido dele, 30/10/2026) e' so VISTA: nao grava nada. Sem ordem = a fila
  // manual da coluna `ordem` (a ronda do estoquista). Mesmo clique da lista de Produtos: asc/desc/sem ordem.
  const [ordem, setOrdem] = useState<{ coluna: ColunaOrdem; dir: 'asc' | 'desc' } | null>(null);
  function alternarOrdem(coluna: ColunaOrdem) {
    setOrdem((o) => (o?.coluna !== coluna ? { coluna, dir: 'asc' } : o.dir === 'asc' ? { coluna, dir: 'desc' } : null));
  }

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const lista = !q ? itens : itens.filter((l) =>
      l.codigo.toLowerCase().includes(q) || (l.descricao ?? '').toLowerCase().includes(q),
    );
    if (!ordem) return lista;
    const sinal = ordem.dir === 'asc' ? 1 : -1;
    return [...lista].sort((a, b) => {
      if (ordem.coluna === 'produtos') return sinal * (a.produtos - b.produtos);
      // Crescente = Ativa primeiro. Empate fica na ordem da ronda (o sort e' estavel).
      if (ordem.coluna === 'status') return sinal * (Number(b.ativo) - Number(a.ativo));
      if (ordem.coluna === 'codigo') return sinal * compararDescricaoNatural(a.codigo, b.codigo);
      // Sem descricao vai pro fim nos dois sentidos — o "—" nao e' um valor pra disputar o topo.
      if (!a.descricao !== !b.descricao) return a.descricao ? -1 : 1;
      return sinal * compararDescricaoNatural(a.descricao, b.descricao);
    });
  }, [itens, busca, ordem]);

  // As setas somem com busca ativa: a lista vira um RECORTE, e reordenar recorte bagunca as linhas
  // escondidas (a mordida do DA-306 no Monitor). Somem tambem com coluna ordenada: o `mover` grava
  // a lista NA ORDEM DA TELA, e a primeira seta trocaria a ronda pela ordem da coluna.
  const podeReordenar = busca.trim() === '' && !ordem && !carregando;

  function cabecalhoOrdenavel(coluna: ColunaOrdem, rotulo: string, direita = false, largura?: number) {
    const atual = ordem?.coluna === coluna ? ordem.dir : null;
    return (
      <th
        style={{ ...sticky.thStyle, cursor: 'pointer', userSelect: 'none', ...(direita ? { width: 90, textAlign: 'right' } : {}), ...(largura ? { width: largura } : {}) }}
        onClick={() => alternarOrdem(coluna)}
        title="Clique para ordenar (crescente / decrescente / ordem da ronda)"
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {rotulo}
          {atual === 'asc' ? <ChevronUp size={12} /> : atual === 'desc' ? <ChevronDown size={12} /> : <ChevronsUpDown size={12} style={{ opacity: 0.4 }} />}
        </span>
      </th>
    );
  }

  function abrirNovo() {
    setEditando(null);
    setForm({ codigo: '', descricao: '' });
    setModal(true);
  }
  function abrirEditar(l: Local) {
    grid.setSelectedId(l.id);
    setEditando(l);
    setForm({ codigo: l.codigo, descricao: l.descricao ?? '' });
    setModal(true);
  }

  async function salvar() {
    try {
      const payload = { codigo: form.codigo, descricao: form.descricao || null };
      if (editando) {
        await api.put(`/api/localizacoes/${editando.id}`, payload);
        toast.sucesso('atualizada');
      } else {
        await api.post('/api/localizacoes', payload);
        toast.sucesso('criada');
      }
      setModal(false);
      void carregar();
    } catch (e: any) { toast.erro(e.message); }
  }

  async function inativar() {
    if (!confirmar) return;
    try {
      const r = await api.delete<{ ok: boolean; produtos_vinculados: number }>(`/api/localizacoes/${confirmar.id}`);
      // Inativar nao desfaz vinculo — avisar em vez de dar um "ok" que esconde os produtos pendurados.
      toast.sucesso(r.produtos_vinculados > 0
        ? `Inativada — mas ${r.produtos_vinculados} produto(s) ainda moram nela. Para juntar num lugar so, use Mesclar.`
        : 'inativada');
      setConfirmar(null);
      void carregar();
    } catch (e: any) { toast.erro(e.message); }
  }

  // A volta do Inativar (pedido dele, 30/10/2026: a inativa nao tinha porta nenhuma de voltar). Mesmo
  // gesto de Unidades de Medida — o `PUT /:id` ja aceitava `ativo`, faltava so o botao.
  async function reativar(l: Local) {
    try {
      await api.put(`/api/localizacoes/${l.id}`, { ativo: true });
      toast.sucesso('reativada');
      void carregar();
    } catch (e: any) { toast.erro(e.message); }
  }

  // Ativar/Inativar EM LOTE (pedido dele, 30/10/2026) sobre os mesmos quadradinhos do Mesclar. Usa as
  // rotas de um-por-um (`PUT /:id` e `DELETE /:id`), entao o efeito e' o mesmo do menu da linha; o
  // lote so' repete. Cada botao conta so' as marcadas que MUDAM de estado.
  const [confirmarInativarLote, setConfirmarInativarLote] = useState(false);
  const [processandoLote, setProcessandoLote] = useState(false);
  async function aplicarLote(ativar: boolean) {
    const alvos = itens.filter((l) => marcadas.has(l.id) && l.ativo !== ativar);
    if (alvos.length === 0) return;
    setProcessandoLote(true);
    let feitos = 0;
    let produtosPendurados = 0;
    const falhas: string[] = [];
    for (const l of alvos) {
      try {
        if (ativar) {
          await api.put(`/api/localizacoes/${l.id}`, { ativo: true });
        } else {
          const r = await api.delete<{ ok: boolean; produtos_vinculados: number }>(`/api/localizacoes/${l.id}`);
          produtosPendurados += r.produtos_vinculados;
        }
        feitos++;
      } catch {
        falhas.push(l.codigo);
      }
    }
    setProcessandoLote(false);
    setConfirmarInativarLote(false);
    if (feitos > 0) {
      toast.sucesso(`${feitos} ${ativar ? 'reativada(s)' : 'inativada(s)'}`
        + (produtosPendurados > 0 ? ` — ${produtosPendurados} produto(s) ainda moram nelas. Para juntar num lugar so, use Mesclar.` : ''));
    }
    // A falha fica nomeada e marcada: o resto se desmarca, so' ela sobra pra tentar de novo.
    if (falhas.length > 0) toast.erro(`Nao consegui ${ativar ? 'reativar' : 'inativar'}: ${falhas.join(', ')}`);
    setMarcadas(new Set(alvos.filter((l) => falhas.includes(l.codigo)).map((l) => l.id)));
    void carregar();
  }

  // Guarda o Y da linha antes do re-render para compensar o scroll depois.
  const pendingScrollRef = useRef<{ seletor: string; topoAntes: number } | null>(null);
  // useLayoutEffect: roda antes da pintura, evitando o flash visual da linha na posicao errada.
  // Vale pras duas setas: a do lugar (muda `itens`) e a do produto dentro do "+" (muda `produtosPorLugar`).
  useLayoutEffect(() => {
    if (!pendingScrollRef.current) return;
    const { seletor, topoAntes } = pendingScrollRef.current;
    pendingScrollRef.current = null;
    const linhaEl = document.querySelector(seletor);
    if (!linhaEl) return;
    const delta = linhaEl.getBoundingClientRect().top - topoAntes;
    if (delta === 0) return;
    // Procura o primeiro ancestral que realmente rola (pode ser um <div> interno, nao o window).
    let container: Element | null = linhaEl.parentElement;
    while (container) {
      const { overflow, overflowY } = getComputedStyle(container);
      if (/auto|scroll/.test(overflow + overflowY) && container.scrollHeight > container.clientHeight) {
        container.scrollTop += delta;
        return;
      }
      container = container.parentElement;
    }
    window.scrollBy(0, delta);
  }, [itens, produtosPorLugar]);

  async function mover(id: number, delta: number) {
    const ids = filtradas.map((l) => l.id);
    const i = ids.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    const porId = new Map(itens.map((l) => [l.id, l]));
    // Captura o topo da linha antes do re-render para manter a linha no mesmo Y da tela.
    const seletor = `tr[data-loc-id="${id}"]`;
    const linhaEl = document.querySelector(seletor);
    if (linhaEl) pendingScrollRef.current = { seletor, topoAntes: linhaEl.getBoundingClientRect().top };
    setItens(ids.map((x) => porId.get(x)!).filter(Boolean));
    try {
      await api.put('/api/localizacoes/reordenar', { ids });
    } catch (e: any) {
      toast.erro(e.message);
      void carregar();
    }
  }

  // A ordem do produto DENTRO da prateleira (pedido dele, 08/10/2026) — a mesma rota e a mesma
  // ordem da tela Localizacoes por Produtos (DA-327): a ordem mora no VINCULO, entao mexer aqui
  // reordena la tambem. Manda a lista INTEIRA (inativos inclusos, que o "+" ja traz): mandar so os
  // ativos renumeraria 1..N por cima da posicao que os inativos ja guardam.
  async function moverProduto(lugarId: number, produtoId: number, delta: number) {
    const lista = produtosPorLugar[lugarId];
    if (!lista) return;
    const ids = lista.map((p) => p.id);
    const i = ids.indexOf(produtoId);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    const porId = new Map(lista.map((p) => [p.id, p]));
    const seletor = `tr[data-lugar-prod="${lugarId}-${produtoId}"]`;
    const linhaEl = document.querySelector(seletor);
    if (linhaEl) pendingScrollRef.current = { seletor, topoAntes: linhaEl.getBoundingClientRect().top };
    setProdutosPorLugar((m) => ({ ...m, [lugarId]: ids.map((x) => porId.get(x)!).filter(Boolean) }));
    try {
      await api.put(`/api/localizacoes/${lugarId}/produtos/reordenar`, { ids });
    } catch (e: any) {
      toast.erro(e.message);
      try {
        const nova = await api.get<ProdutoDoLugar[]>(`/api/localizacoes/${lugarId}/produtos`, { query: { incluir_inativos: true } });
        setProdutosPorLugar((m) => ({ ...m, [lugarId]: nova }));
      } catch { /* o toast do erro original ja avisou */ }
    }
  }

  function alternarMarca(id: number) {
    setMarcadas((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  const selecionadas = itens.filter((l) => marcadas.has(l.id));
  const alvo = selecionadas.find((l) => l.id === sobrevivente) ?? null;
  const absorvidas = selecionadas.filter((l) => l.id !== sobrevivente);
  const produtosAfetados = absorvidas.reduce((s, l) => s + l.produtos, 0);
  const paraAtivar = selecionadas.filter((l) => !l.ativo);
  const paraInativar = selecionadas.filter((l) => l.ativo);
  const produtosNasInativadas = paraInativar.reduce((s, l) => s + l.produtos, 0);
  const todasVisiveisMarcadas = filtradas.length > 0 && filtradas.every((l) => marcadas.has(l.id));
  function alternarTodasVisiveis() {
    setMarcadas((prev) => {
      const n = new Set(prev);
      if (todasVisiveisMarcadas) filtradas.forEach((l) => n.delete(l.id));
      else filtradas.forEach((l) => n.add(l.id));
      return n;
    });
  }

  function abrirMesclar() {
    // O sobrevivente sugerido e o que tem MAIS produtos: mesclar pro lado cheio move menos gente.
    const maior = [...selecionadas].sort((a, b) => b.produtos - a.produtos)[0];
    setSobrevivente(maior?.id ?? null);
    setModalMesclar(true);
  }

  async function mesclar() {
    if (!sobrevivente || absorvidas.length === 0) return;
    setMesclando(true);
    try {
      const r = await api.post<{ produtos_movidos: number; localizacoes_apagadas: number }>(
        '/api/localizacoes/mesclar',
        { manter: sobrevivente, remover: absorvidas.map((l) => l.id) },
      );
      toast.sucesso(`${r.produtos_movidos} produto(s) movido(s) para ${alvo?.codigo}; ${r.localizacoes_apagadas} localizacao(oes) absorvida(s).`);
      setModalMesclar(false);
      setMarcadas(new Set());
      void carregar();
    } catch (e: any) { toast.erro(e.message); }
    finally { setMesclando(false); }
  }

  const sticky = useStickyHeader();

  // Teclado desligado com modal aberto: o Enter num botao do modal reabriria a edicao.
  const grid = useGridNavegavel<Local>({
    itens: filtradas,
    getId: (l) => l.id,
    onAtivar: abrirEditar,
    ativo: !modal && !modalMesclar && !confirmar && !confirmarInativarLote,
  });

  return (
    <div>
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo">Localizacoes</h2>
          <div className="pagina-acoes">
            <input type="search" placeholder="Buscar..." className="busca" value={busca} onChange={(e) => setBusca(e.target.value)} />
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={mostrarInativas} onChange={(e) => setMostrarInativas(e.target.checked)} />
              Mostrar inativas
            </label>
            {marcadas.size >= 2 && (
              <button type="button" className="btn-secundario" onClick={abrirMesclar} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Merge size={15} /> Mesclar ({marcadas.size})
              </button>
            )}
            {paraAtivar.length > 0 && (
              <button type="button" className="btn-secundario" disabled={processandoLote} onClick={() => void aplicarLote(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                <CheckCircle2 size={15} /> Ativar ({paraAtivar.length})
              </button>
            )}
            {paraInativar.length > 0 && (
              <button type="button" className="btn-secundario" disabled={processandoLote} onClick={() => setConfirmarInativarLote(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                <Ban size={15} /> Inativar ({paraInativar.length})
              </button>
            )}
            <button type="button" className="btn-primario" onClick={abrirNovo}>+ Nova</button>
          </div>
        </div>
      </div>

      {carregando ? <p className="muted">Carregando...</p> : filtradas.length === 0 ? <p className="muted">Nenhuma localizacao cadastrada.</p> : (
        <table className="tabela" style={sticky.tableStyle}>
          <thead><tr>
            <th style={{ ...sticky.thStyle, width: 34 }}>
              <input type="checkbox" checked={todasVisiveisMarcadas} onChange={alternarTodasVisiveis}
                title={todasVisiveisMarcadas ? 'Desmarcar todas as da lista' : 'Marcar todas as da lista'} />
            </th>
            <th style={{ ...sticky.thStyle, width: 56 }}></th>
            {cabecalhoOrdenavel('codigo', 'Codigo')}
            {cabecalhoOrdenavel('descricao', 'Descricao')}
            {cabecalhoOrdenavel('produtos', 'Produtos', true)}
            {cabecalhoOrdenavel('status', 'Status', false, 100)}
            <th style={{ ...sticky.thStyle, width: 80, textAlign: 'right' }}>Acoes</th>
          </tr></thead>
          <tbody>
            {filtradas.map((l, idx) => {
              const acoes: AcaoLinha[] = [
                { label: 'Editar', icon: Pencil, onClick: () => abrirEditar(l) },
                { label: 'Inativar', icon: Ban, perigoso: true, oculto: !l.ativo, onClick: () => setConfirmar(l) },
                { label: 'Reativar', icon: CheckCircle2, oculto: l.ativo, onClick: () => void reativar(l) },
              ];
              const aberto = abertos.has(l.id);
              const produtosDoLugar = produtosPorLugar[l.id];
              return (
                <Fragment key={l.id}>
                <tr data-loc-id={l.id} {...grid.rowProps(l)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={marcadas.has(l.id)} onChange={() => alternarMarca(l.id)} />
                  </td>
                  <td onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                    {podeReordenar && (
                      <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
                        <button type="button" title="Mover para cima" disabled={idx === 0} onClick={() => void mover(l.id, -1)}
                          style={{ background: 'none', border: 'none', padding: 0, lineHeight: 0, color: 'inherit', cursor: idx === 0 ? 'default' : 'pointer', opacity: idx === 0 ? 0.25 : 1 }}>
                          <ChevronUp size={13} />
                        </button>
                        <button type="button" title="Mover para baixo" disabled={idx === filtradas.length - 1} onClick={() => void mover(l.id, 1)}
                          style={{ background: 'none', border: 'none', padding: 0, lineHeight: 0, color: 'inherit', cursor: idx === filtradas.length - 1 ? 'default' : 'pointer', opacity: idx === filtradas.length - 1 ? 0.25 : 1 }}>
                          <ChevronDown size={13} />
                        </button>
                      </span>
                    )}
                  </td>
                  <td style={{ fontWeight: 600 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      {/* Lugar reservado mesmo sem produto, senao os codigos perdem o prumo. */}
                      <span style={{ width: 22, display: 'inline-flex' }} onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                        {l.produtos > 0 && (
                          <button
                            type="button"
                            onClick={() => alternarProdutos(l.id)}
                            title={aberto ? 'Ocultar os produtos desta prateleira' : 'Ver os produtos desta prateleira'}
                            aria-expanded={aberto}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              width: 22, height: 22, padding: 0, borderRadius: 4,
                              border: '1px solid var(--input-border, #d1d5db)',
                              background: aberto ? 'var(--accent, #2563eb)' : 'transparent',
                              color: aberto ? '#fff' : 'var(--accent, #2563eb)',
                              cursor: 'pointer',
                            }}
                          >
                            {aberto ? <Minus size={12} /> : <Plus size={12} />}
                          </button>
                        )}
                      </span>
                      {l.codigo}
                    </span>
                  </td>
                  <td>{l.descricao || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{l.produtos}</td>
                  <td><StatusBadge ativo={l.ativo} labelAtivo="Ativa" labelInativo="Inativa" /></td>
                  <td style={{ textAlign: 'right' }}>
                    <MenuAcoesLinha acoes={acoes} />
                  </td>
                </tr>
                {aberto && (
                  <tr>
                    <td colSpan={7} style={{ padding: 0 }}>
                      <div style={{ padding: '8px 16px 12px 56px', background: 'var(--page-bg)' }}>
                        {produtosDoLugar === undefined || produtosDoLugar === null ? (
                          <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Carregando produtos...</div>
                        ) : produtosDoLugar.length === 0 ? (
                          <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Nenhum produto nesta prateleira</div>
                        ) : (
                          <table style={{ width: '100%', fontSize: 13, background: 'var(--panel-bg, #fff)', borderRadius: 6, border: '1px solid var(--input-border, #e5e7eb)' }}>
                            <thead>
                              <tr style={{ background: 'var(--grid-header-bg, #f3f4f6)', color: 'var(--grid-header-fg, #4b5563)' }}>
                                <th style={{ padding: '6px 8px', width: 36 }}></th>
                                <th style={{ textAlign: 'center', padding: '6px 8px', width: 32, fontSize: 11, fontWeight: 600 }}>#</th>
                                <th style={{ padding: '6px 8px', width: 60, fontSize: 11, fontWeight: 600 }}></th>
                                <th style={{ textAlign: 'left', padding: '6px 12px', width: 80, fontSize: 11, fontWeight: 600 }}>Cod.</th>
                                <th style={{ textAlign: 'left', padding: '6px 12px', fontSize: 11, fontWeight: 600 }}>
                                  Descricao <span style={{ marginLeft: 8, opacity: 0.6, fontWeight: 400 }}>({l.codigo})</span>
                                </th>
                                <th style={{ textAlign: 'left', padding: '6px 12px', width: 150, fontSize: 11, fontWeight: 600 }}>Marca</th>
                                <th style={{ textAlign: 'right', padding: '6px 12px', width: 110, fontSize: 11, fontWeight: 600 }}>Estoque</th>
                                <th style={{ textAlign: 'left', padding: '6px 12px', width: 160, fontSize: 11, fontWeight: 600 }}>Tambem em</th>
                              </tr>
                            </thead>
                            <tbody>
                              {produtosDoLugar.map((p, i) => (
                                <tr key={p.id} data-lugar-prod={`${l.id}-${p.id}`} style={{ borderTop: '1px solid var(--input-border, #f3f4f6)' }}>
                                  <td style={{ padding: '4px 8px' }} onDoubleClick={(e) => e.stopPropagation()}>
                                    <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
                                      <button type="button" title="Mover para cima" disabled={i === 0} onClick={() => void moverProduto(l.id, p.id, -1)}
                                        style={{ background: 'none', border: 'none', padding: 0, lineHeight: 0, color: 'inherit', cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.25 : 1 }}>
                                        <ChevronUp size={13} />
                                      </button>
                                      <button type="button" title="Mover para baixo" disabled={i === produtosDoLugar.length - 1} onClick={() => void moverProduto(l.id, p.id, 1)}
                                        style={{ background: 'none', border: 'none', padding: 0, lineHeight: 0, color: 'inherit', cursor: i === produtosDoLugar.length - 1 ? 'default' : 'pointer', opacity: i === produtosDoLugar.length - 1 ? 0.25 : 1 }}>
                                        <ChevronDown size={13} />
                                      </button>
                                    </span>
                                  </td>
                                  <td style={{ padding: '6px 8px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>{i + 1}</td>
                                  <td style={{ padding: '4px 8px' }}>
                                    {p.foto_url ? (
                                      <FotoProduto url={p.foto_url} tam={48} />
                                    ) : (
                                      <div style={{ width: 48, height: 48, borderRadius: 4, background: '#f3f4f6', border: '1px solid var(--input-border, #e5e7eb)' }} />
                                    )}
                                  </td>
                                  <td style={{ padding: '6px 12px', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 12 }}>{p.codigo_interno || '—'}</td>
                                  <td style={{ padding: '6px 12px' }}>
                                    {p.descricao}
                                    {p.status !== 'ativo' && (
                                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: '#000', backgroundColor: '#d1d5db', padding: '1px 6px', borderRadius: 999 }}>inativo</span>
                                    )}
                                  </td>
                                  <td style={{ padding: '6px 12px' }}>{p.marca_nome || '—'}</td>
                                  <td className="cel-num" style={{ padding: '6px 12px', textAlign: 'right' }}>
                                    {p.estoque_efetivo == null ? '—' : Number(p.estoque_efetivo).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}
                                    {p.unidade_sigla ? ` ${p.unidade_sigla}` : ''}
                                  </td>
                                  <td style={{ padding: '6px 12px', fontSize: 12 }}>{p.outros_lugares || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      <Modal aberto={modal} onFechar={() => setModal(false)} titulo={editando ? 'Editar Localizacao' : 'Nova Localizacao'} largura={520}
        rodape={
          <>
            <button type="button" className="btn-secundario" onClick={() => setModal(false)}>Cancelar</button>
            <button type="button" className="btn-primario" onClick={salvar} disabled={!form.codigo.trim()}>Salvar</button>
          </>
        }
      >
        <div className="form-grid">
          <label>
            Codigo
            <UpperInput value={form.codigo} onChange={(v) => setForm({ ...form, codigo: v })} maxLength={30} autoFocus />
          </label>
          <label className="full">
            Descricao
            <UpperInput value={form.descricao} onChange={(v) => setForm({ ...form, descricao: v })} maxLength={100} />
          </label>
          <p className="muted full" style={{ fontSize: 12, margin: 0 }}>
            O codigo e o que a vendedora le na tela — ele precisa se bastar sozinho, sem contexto.
            A descricao e conforto pra quem cadastra. A ordem da lista e a ordem da ronda do estoquista.
          </p>
        </div>
      </Modal>

      <Modal aberto={modalMesclar} onFechar={() => setModalMesclar(false)} titulo="Mesclar localizacoes" largura={560}
        rodape={
          <>
            <button type="button" className="btn-secundario" onClick={() => setModalMesclar(false)}>Cancelar</button>
            <button type="button" className="btn-primario" onClick={mesclar} disabled={!sobrevivente || absorvidas.length === 0 || mesclando}>
              {mesclando ? 'Mesclando...' : 'Mesclar'}
            </button>
          </>
        }
      >
        <div>
          <p style={{ marginTop: 0, fontSize: 13 }}>Qual destas e o lugar de verdade? As outras somem e os produtos delas passam pra ela.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            {selecionadas.map((l) => (
              <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                <input type="radio" name="sobrevivente" checked={sobrevivente === l.id} onChange={() => setSobrevivente(l.id)} />
                <span style={{ fontWeight: 600 }}>{l.codigo}</span>
                {l.descricao && <span className="muted">{l.descricao}</span>}
                <span className="muted">— {l.produtos} produto(s)</span>
              </label>
            ))}
          </div>
          {/* A previa em linguagem natural ANTES de executar (padrao fotos-consolidar): o operador
              precisa ver o tamanho do estrago, nao um "confirma?" cego. */}
          {alvo && (
            <p style={{ fontSize: 13, backgroundColor: 'var(--positive-bg)', color: '#000', padding: 10, borderRadius: 4, margin: 0 }}>
              Vou mover <strong>{produtosAfetados} produto(s)</strong> de{' '}
              <strong>{absorvidas.map((l) => l.codigo).join(', ')}</strong> para <strong>{alvo.codigo}</strong>{' '}
              e apagar {absorvidas.length} localizacao(oes). Os produtos nao sao alterados — so passam a apontar pra {alvo.codigo}.
            </p>
          )}
        </div>
      </Modal>

      <ConfirmModal aberto={confirmarInativarLote} titulo="Inativar em lote"
        mensagem={`Inativar ${paraInativar.length} localizacao(oes): ${paraInativar.map((l) => l.codigo).join(', ')}?`
          + (produtosNasInativadas > 0 ? ` ${produtosNasInativadas} produto(s) continuam morando nelas.` : '')}
        textoConfirmar={processandoLote ? 'Inativando...' : 'Inativar'} perigoso
        onConfirmar={() => void aplicarLote(false)} onCancelar={() => setConfirmarInativarLote(false)} />

      <ConfirmModal aberto={!!confirmar} titulo="Inativar" mensagem={`Inativar "${confirmar?.codigo}"?`} textoConfirmar="Inativar" perigoso onConfirmar={inativar} onCancelar={() => setConfirmar(null)} />
    </div>
  );
}
